import { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    IconButton,
    Avatar,
    Chip,
} from '@mui/material';
import {
    VpnKey,
    Add,
    Pin,
    QrCode,
    CreditCard,
    Fingerprint,
    ConfirmationNumber,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttUnlockerDialog } from './all';

type UnlockerType =
    | 'PIN'
    | 'QR'
    | 'CARD'
    | 'TICKET_CARD'
    | 'FINGERPRINT'
    | 'TICKET_ONE_USE';

type TypeConfig = {
    letter: string;
    color: string;
    icon: React.ReactNode;
};

const typeConfig: Record<UnlockerType, TypeConfig> = {
    PIN: { letter: 'P', color: '#4CAF50', icon: <Pin /> },
    QR: { letter: 'Q', color: '#2196F3', icon: <QrCode /> },
    CARD: { letter: 'C', color: '#FF9800', icon: <CreditCard /> },
    TICKET_CARD: {
        letter: 'T',
        color: '#9C27B0',
        icon: <ConfirmationNumber />,
    },
    FINGERPRINT: { letter: 'F', color: '#E91E63', icon: <Fingerprint /> },
    TICKET_ONE_USE: {
        letter: '1',
        color: '#607D8B',
        icon: <ConfirmationNumber />,
    },
};

const defaultTypeConfig: TypeConfig = {
    letter: '?',
    color: '#9E9E9E',
    icon: <VpnKey />,
};

type Unlocker = {
    id: number;
    name?: string;
    code?: string;
    type?: UnlockerType;
    [key: string]: unknown;
};

type Props = {
    unlockers?: Unlocker[];
    idUser?: number;
    idSC?: number;
    showCreatingButton?: boolean;
};

export function SttUnlockerChips({
    unlockers = [],
    idUser,
    idSC,
    showCreatingButton,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [unlockerDialogOpened, setUnlockerDialogOpened] =
        useState<boolean>(false);
    const [showingUnlockerId, setShowingUnlockerId] = useState<
        number | undefined
    >();

    const onCreated = (uk: Unlocker) => {
        (unlockers as any).push(uk);
    };

    const openUnlocker = (unlocker: Unlocker) => {
        setUnlockerDialogOpened(true);
        setShowingUnlockerId(unlocker.id);
    };

    const onDeleted = (unlocker: Unlocker) => {
        const idxToDelete = (unlockers ?? []).findIndex(
            (e: any) => e.id === unlocker.id,
        );
        if (idxToDelete >= 0) {
            (unlockers as any).splice(idxToDelete, 1);
        }
    };

    const openToCreate = () => {
        setShowingUnlockerId(undefined);
        setUnlockerDialogOpened(true);
    };

    // Empty state
    if ((!unlockers || unlockers.length === 0) && !showCreatingButton) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    color: 'text.secondary',
                }}
            >
                <VpnKey sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1">
                    {t('generic.noUnlockersAssigned')}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ p: 1 }}>
                <Grid container spacing={2}>
                    {(unlockers ?? []).map((unlocker, idx) => {
                        const config = unlocker.type
                            ? typeConfig[unlocker.type] || defaultTypeConfig
                            : defaultTypeConfig;

                        return (
                            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={idx}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 2,
                                            borderColor: config.color,
                                        },
                                    }}
                                    onClick={() => openUnlocker(unlocker)}
                                >
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            p: 2,
                                            '&:last-child': { pb: 2 },
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                mb: 1,
                                                bgcolor: `${config.color}20`,
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 20,
                                                    color: config.color,
                                                },
                                            }}
                                        >
                                            {config.icon}
                                        </Avatar>
                                        <Chip
                                            size="small"
                                            label={
                                                unlocker.type
                                                    ? t(`UT.${unlocker.type}`)
                                                    : t('generic.unknown')
                                            }
                                            sx={{
                                                mb: 1,
                                                bgcolor: config.color,
                                                color: 'white',
                                                fontSize: '0.65rem',
                                                height: 20,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                            }}
                                        >
                                            {unlocker.name ||
                                                unlocker.code ||
                                                `#${unlocker.id}`}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}

                    {showCreatingButton && (
                        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    borderStyle: 'dashed',
                                    '&:hover': {
                                        boxShadow: 2,
                                        borderColor: 'primary.dark',
                                        bgcolor: 'action.hover',
                                    },
                                }}
                                onClick={openToCreate}
                            >
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        p: 2,
                                        minHeight: 100,
                                        '&:last-child': { pb: 2 },
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{
                                            bgcolor: 'grey.100',
                                            mb: 1,
                                        }}
                                    >
                                        <Add />
                                    </IconButton>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t('generic.add')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <SttUnlockerDialog
                open={unlockerDialogOpened}
                idUnlocker={showingUnlockerId}
                idUser={idUser}
                idSC={idSC}
                onClose={() => setUnlockerDialogOpened(false)}
                onSave={(uk: any) => onCreated(uk)}
                onDelete={onDeleted as any}
            />
        </>
    );
}
